import React, { lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { PrivateRoute } from "src/components";
import Assignments from "src/modules/assignments";
import CreateAssignment from "src/modules/createAssignment";
import ViewAssignment from "../modules/viewAssignment/ViewAssignment";
import SchoolInfo from "../modules/schools/SchoolInfo/SchoolInfo";
import { USER_TYPES } from "../constants";
import TeacherResources from "../modules/teacherResources/teacherResources";
import Reports from "../modules/reports/Reports";
import GroupReports from "src/modules/reports/GroupReports";
import GroupReportsMoreDetails from "src/modules/reports/GroupReportsMoreDetails";
import GriffleLogin from "src/modules/griffle/GriffleLogin/GriffleLogin";
import GriffleSso from "src/modules/griffle/GriffleSso/GriffleSso";
import GrifflePrivateRoute from "src/modules/griffle/GrifflePrivateRoute/GrifflePrivateRoute";
import GriffleLayout from "src/modules/griffle/GriffleLayout/GriffleLayout";
import GriffleDashboard from "src/modules/griffle/GriffleDashboard/GriffleDashboard";
import ManenoLayout from "src/modules/maneno/ManenoLayout/ManenoLayout";

const NotFound = lazy(() => import("src/components/NotFound"));
const Login = lazy(() => import("src/modules/login"));
const Sso = lazy(() => import("src/modules/sso"));
const ForgotPassword = lazy(() => import("src/modules/forgotPassword"));
const Dashboard = lazy(() => import("src/modules/dashboard"));
const Library = lazy(() => import("src/modules/library"));
const LibraryByCategory = lazy(() => import("src/modules/library/LibraryByCategory"));
const ProgressReportStudents = lazy(() => import("src/modules/progressReportStudents"));
const ProgressReportClass = lazy(() => import("src/modules/progressReportClass"));
const StudentProfile = lazy(() => import("src/modules/studentProfile"));
const BookDetails = lazy(() => import("src/modules/bookDetails"));
const AssignmentDetails = lazy(() => import("src/modules/assignmentDetails"));
const BookAssignmentDetails = lazy(() => import("src/modules/bookAssignmentDetails"));
const Schools = lazy(() => import("src/modules/schools"));

const isGriffleDomain = () =>
  window.location.hostname.includes("griffle") || window.location.hostname.includes("readgriffin");

const AppRouter: React.FC = () => {
  const { pathname, search, hash } = useLocation();

  if (isGriffleDomain() && !pathname.startsWith("/griffle")) {
    return <Navigate replace to={`/griffle${pathname}${search}${hash}`} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate replace to="/dashboard" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/sso" element={<Sso />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <Dashboard />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/library"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <Library />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/library/:catId"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <LibraryByCategory />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/book/:id"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <BookDetails />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/progress-report-students"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <ProgressReportStudents />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/progress-report-detailed"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <GroupReports />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/progress-report-more-details"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <GroupReportsMoreDetails />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/progress-report-class"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <ProgressReportClass />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/progress-report-students/:id"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <StudentProfile />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/progress-report-students/:studentId/assignments/quiz/:challengeId"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <AssignmentDetails />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/book/:id/:id"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <BookAssignmentDetails />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/teacher-resources"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <TeacherResources />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/assignments"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <Assignments />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/assignments/:id"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <ViewAssignment />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/assignments/create"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <CreateAssignment />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/assignments/create/:id/:editMode?"
        element={
          <PrivateRoute>
            <ManenoLayout>
              <CreateAssignment isEditing />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="schools"
        element={
          <PrivateRoute acceptedUserTypes={[USER_TYPES.SCHOOL_ADMIN, USER_TYPES.DISTRIBUTOR_ADMIN]}>
            <ManenoLayout>
              <Schools />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="schools/:id"
        element={
          <PrivateRoute acceptedUserTypes={[USER_TYPES.SCHOOL_ADMIN, USER_TYPES.DISTRIBUTOR_ADMIN]}>
            <ManenoLayout>
              <SchoolInfo />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="reports"
        element={
          <PrivateRoute acceptedUserTypes={[USER_TYPES.SCHOOL_ADMIN, USER_TYPES.DISTRIBUTOR_ADMIN]}>
            <ManenoLayout>
              <Reports />
            </ManenoLayout>
          </PrivateRoute>
        }
      />
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate replace to="/404" />} />

      {/* ── Griffle skin routes ─────────────────────────────── */}
      <Route path="/griffle/login" element={<GriffleLogin />} />
      <Route path="/griffle/sso" element={<GriffleSso />} />
      <Route
        path="/griffle/dashboard"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <GriffleDashboard />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/library"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <Library />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/library/:catId"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <LibraryByCategory />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/book/:id"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <BookDetails />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/book/:id/:id"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <BookAssignmentDetails />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/assignments"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <Assignments />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/assignments/:id"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <ViewAssignment />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/assignments/create"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <CreateAssignment />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/assignments/create/:id/:editMode?"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <CreateAssignment isEditing />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/progress-report-students"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <ProgressReportStudents />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/progress-report-students/:id"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <StudentProfile />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/progress-report-students/:studentId/assignments/quiz/:challengeId"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <AssignmentDetails />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/progress-report-detailed"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <GroupReports />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/progress-report-more-details"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <GroupReportsMoreDetails />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/progress-report-class"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <ProgressReportClass />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/teacher-resources"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <TeacherResources />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/reports"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <Reports />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/reports-detailed"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <GroupReports />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/reports-more-details"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <GroupReportsMoreDetails />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/schools"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <Schools />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route
        path="/griffle/schools/:id"
        element={
          <GrifflePrivateRoute>
            <GriffleLayout>
              <SchoolInfo />
            </GriffleLayout>
          </GrifflePrivateRoute>
        }
      />
      <Route path="/griffle" element={<Navigate replace to="/griffle/dashboard" />} />
      <Route path="/griffle/*" element={<Navigate replace to="/griffle/dashboard" />} />
    </Routes>
  );
};

export default AppRouter;
